import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { Field, FieldProps, Form } from "react-final-form";
import { format } from "date-fns/format";
import { z } from "zod";
import { PEOPLE_PAGE_QUERY } from "../../../../features/graphql/query/person/personQueries";
import LoadingButton from "@mui/lab/LoadingButton";
import { MANAGE_USER_FIELDS } from "../../../StaffPages/constants/constants";
import InputField from "../../../../components/Form/InputField";
import { useMutation, useQuery } from "@apollo/client";
import { GET_ROLES } from "../../../../features/graphql/query/roles/getRoles";
import { AddPersonMutation, AddPersonMutationVariables, GetRolesQuery, PersonRole } from "../../../../__generated__/graphql";
import { ADD_PERSON_MUTATION } from "../../../../features/graphql/mutation/person/addPerson";
import { useSlideInContext } from "../../../../context/SlideInContext";
import { SelectOption, getDefaultValue } from "../../../../components/Form/SelectField/SelectField";
import { ApiValidationError, mapApiFieldValidationErrors } from "../../../../components/Form";
import { useManageModal } from "../../../Manage/components/ManageModal";
const manageDetailsSchema = z.object({
  firstName: z.string().trim().nonempty().max(255),
  middleName: z.string().trim().max(255).nullish(),
  lastName: z.string().trim().nonempty().max(255),
  jobTitle: z.string().trim().max(255).nullish(),
  email: z.string().nonempty().email(),
  roleType: z.enum(["ADMIN", "STANDARD", 'OFFICE_ADMIN', "SUPPORT_ADMIN"]),
  startDate: z.coerce.date()
});

type EditPersonSchema = z.infer<typeof manageDetailsSchema>;

const formValidator = <T extends z.ZodType<any, any>>(schema: T) => (values: any) => {
  try {
    schema.parse(values);
  } catch (err) {
    return (err as z.ZodError).formErrors.fieldErrors;
  }
};

const mapSelectOptions = (options: any[]): SelectOption[] => options.map(x => ({
  label: x.name,
  value: x.normalizedName.replace(/\s+/g, "_"),
  isDefault: x.default,
})) ?? []

// const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const AddUser = () => {
  const [fields, setFields] = useState<FieldProps<string, any>>();
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const { toggleDrawer } = useSlideInContext();
  const { data } = useQuery<GetRolesQuery>(GET_ROLES);
  const { openModal } = useManageModal()
  const [addPerson] = useMutation<AddPersonMutation, AddPersonMutationVariables>(ADD_PERSON_MUTATION, {
    refetchQueries: [PEOPLE_PAGE_QUERY]
  })

  useEffect(() => {
    if (!data?.roles) return;

    var options = mapSelectOptions(data?.roles ?? []).filter((x) => x.label != "Super Admin");
    setFields(() => MANAGE_USER_FIELDS.map((field) => {
      if (field.name !== "roleType") return field;

      return {
        ...field,
        options
      };
    }));

    setRoleOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MANAGE_USER_FIELDS, data?.roles]);

  const createNewUser = async (values: EditPersonSchema) => {

    const response = await addPerson({
      variables: {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        role: values.roleType as PersonRole,
        email: values.email,
        startDate: values.startDate ? new Date(values.startDate).toISOString() : null,
      },
      onError(error, clientOptions) {
        openModal({ type: "GENERAL_ERROR" })
        console.log('Query error: ', error, clientOptions);
      }
    });

    // If type of "ValidationError" exists in the errors payload field
    // map errors to dicitonary and return. Final form will then 
    // render the errors to the each stated field
    // Note: must match form definition
    if (response.data?.addPerson.errors) {
      const validationError = response.data.addPerson.errors
        .find(e => e.__typename === "ValidationError") as ApiValidationError;
      const businessRuleError = response.data.addPerson.errors
        .find(e => e.__typename === "EntityBusinessRuleError") as ApiValidationError;
      if (validationError) {
        const fieldErrors = validationError
          ? mapApiFieldValidationErrors(validationError)
          : null;

        return fieldErrors;
      } else if (businessRuleError) {
        openModal({ type: "USER_CAP" })
      } else {
        openModal({ type: "GENERAL_ERROR" })
      }
    }

    toggleDrawer();
  };

  return (
    <div style={{ color: "#fff", paddingTop: "16px" }}>
      <Form
        initialValues={{
          roleType: getDefaultValue(roleOptions),
          startDate: format(new Date(), 'yyyy-MM-dd')
        }}
        onSubmit={createNewUser}
        validate={formValidator(manageDetailsSchema)}
        render={({ handleSubmit, form, submitting }) => (
          <form onSubmit={handleSubmit}>
            <Grid style={{ marginTop: 0 }} container spacing={4} rowSpacing={4}>
              {fields && fields.map((field: FieldProps<string, any>, i: number) => (
                <Grid
                  key={`add-user-field${i + 20}`}
                  size={field.grid.xs}
                >
                  <Field
                    autoComplete="off"
                    {...field} />
                </Grid>
              ))}
              <Grid size={6}>
                <Field
                  name="startDate"
                  label="Start Date"
                  type="date"
                  component={InputField}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid
                size={12}
                sx={{ display: "flex", justifyContent: "flex-start" }}
              >
                <LoadingButton
                  loading={submitting}
                  disableElevation
                  variant="contained"
                  size="large"
                  type="submit"
                  onClick={() => form.submit()}
                  sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.light",
                    },
                  }}
                >
                  Save
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      />
    </div>
  );
};

export default AddUser;
